import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabaseClient'

const API_URL = process.env.REACT_APP_BACKEND_URL

/**
 * Individual Testovanie 9 custom offer page.
 *
 * Access control is enforced server-side (GET /api/individual-offer/:token
 * requires JWT + current_user.id === offer.target_user_id). This page
 * mirrors the same gates for UX reasons:
 *   - unauthenticated users are routed to login
 *   - mismatched users see a friendly 403 state
 */
const IndividualOfferPage = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()

  const [offer, setOffer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [validationError, setValidationError] = useState(null)

  useEffect(() => {
    if (authLoading) return
    if (!user) {
      setValidationError('login_required')
      setLoading(false)
      return
    }
    loadOffer()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, authLoading])

  const loadOffer = async () => {
    setLoading(true)
    setError(null)
    setValidationError(null)
    try {
      // Fetch with JWT so the backend can enforce identity.
      const { data: sess } = await supabase.auth.getSession()
      const authToken = sess?.session?.access_token
      if (!authToken) {
        setValidationError('login_required')
        setLoading(false)
        return
      }
      const resp = await fetch(`${API_URL}/api/individual-offer/${token}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      if (resp.status === 404) {
        setError('not_found')
        return
      }
      if (resp.status === 403) {
        setValidationError('wrong_user')
        return
      }
      if (!resp.ok) {
        setError('unknown')
        return
      }
      const data = await resp.json()
      setOffer(data)
      // status + expiry gates
      if (data.status === 'used') setValidationError('used')
      else if (data.status === 'cancelled') setValidationError('cancelled')
      else if (data.status !== 'active') setValidationError('invalid_status')
      else if (new Date() > new Date(data.valid_until)) setValidationError('expired')
    } catch (e) {
      console.error('Error loading individual offer:', e)
      setError('unknown')
    } finally {
      setLoading(false)
    }
  }

  const handleProceedToPayment = async () => {
    try {
      setLoading(true)
      const { data: sess } = await supabase.auth.getSession()
      const authToken = sess?.session?.access_token
      if (!authToken) {
        alert('Prihlásenie vypršalo. Prosím, prihláste sa znova.')
        navigate('/login', { state: { from: `/individual-offer/${token}` } })
        return
      }

      const response = await fetch(`${API_URL}/api/individual-offer-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ token, origin_url: window.location.origin }),
      })
      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        alert(err.detail || 'Chyba pri vytváraní platby')
        return
      }
      const data = await response.json()
      if (data.url) {
        window.location.href = data.url
      } else {
        alert('Chyba: Nepodarilo sa vytvoriť platobnú session.')
      }
    } catch (e) {
      console.error('Checkout error:', e)
      alert('Nastala chyba pri vytváraní platby. Skúste to prosím neskôr.')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Načítavam ponuku...</p>
        </div>
      </div>
    )
  }

  if (error === 'not_found') {
    return (
      <ErrorCard
        emoji="❌"
        title="Ponuka nenájdená"
        body="Táto ponuka neexistuje."
        onBack={() => navigate('/')}
      />
    )
  }
  if (error === 'unknown') {
    return (
      <ErrorCard
        emoji="⚠️"
        title="Chyba"
        body="Pri načítaní ponuky došlo k chybe. Skúste to prosím neskôr."
        onBack={() => navigate('/')}
      />
    )
  }

  if (validationError === 'login_required') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">🔐</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Prihlásenie potrebné</h1>
            <p className="text-gray-600 mb-6">
              Pre pokračovanie sa prihláste účtom, pre ktorý bola táto ponuka vytvorená.
            </p>
            <button
              data-testid="individual-offer-login-btn"
              onClick={() => navigate('/login', { state: { from: `/individual-offer/${token}` } })}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
            >
              Prihlásiť sa
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (validationError === 'wrong_user') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Neplatný účet</h1>
            <p className="text-gray-600 mb-6">
              Táto ponuka nie je dostupná pre prihlásený účet.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Prihláste sa prosím účtom, pre ktorý bola ponuka vytvorená.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                data-testid="individual-offer-switch-login-btn"
                onClick={() => navigate('/login', { state: { from: `/individual-offer/${token}` } })}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                Prihlásiť sa iným účtom
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
              >
                Späť
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (validationError === 'used') {
    return <ErrorCard emoji="🔒" title="Ponuka už bola použitá"
      body="Táto ponuka už bola použitá a nie je možné ju použiť znova." onBack={() => navigate('/')} />
  }
  if (validationError === 'cancelled') {
    return <ErrorCard emoji="🚫" title="Ponuka bola zrušená"
      body="Táto ponuka bola zrušená." onBack={() => navigate('/')} />
  }
  if (validationError === 'invalid_status') {
    return <ErrorCard emoji="⚠️" title="Ponuka nie je aktívna"
      body="Táto ponuka momentálne nie je aktívna." onBack={() => navigate('/')} />
  }
  if (validationError === 'expired') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">⏰</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Ponuka vypršala</h1>
            <p className="text-gray-600 mb-6">Platnosť tejto ponuky vypršala.</p>
            {offer && (
              <p className="text-sm text-gray-500 mb-6">
                Platná bola do: {new Date(offer.valid_until).toLocaleDateString('sk-SK', {
                  day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                })}
              </p>
            )}
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold"
            >
              Späť na hlavnú stránku
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Success state — show the offer.
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-testid="individual-offer-card">
          <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-8 px-8">
            <div className="text-center">
              <div className="text-5xl mb-4">🎉</div>
              <h1 className="text-3xl font-bold mb-2">Individuálna ponuka pre vás</h1>
              <p className="text-blue-100">
                Pripravili sme pre vás individuálnu cenu za prístup k Testovaniu 9.
              </p>
            </div>
          </div>

          <div className="p-8">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg mb-6">
              <div className="grid gap-4">
                <div>
                  <span className="text-sm font-semibold text-gray-700">Produkt:</span>
                  <p className="text-lg font-bold text-gray-900 mt-1">Testovanie 9</p>
                </div>
                <div className="border-t border-blue-200 pt-4">
                  <span className="text-sm font-semibold text-gray-700">Školský rok:</span>
                  <p className="text-lg font-bold text-gray-900 mt-1" data-testid="individual-offer-school-year">
                    {offer.school_year}
                  </p>
                </div>
                <div className="border-t border-blue-200 pt-4">
                  <span className="text-sm font-semibold text-gray-700">Cena:</span>
                  <p className="text-3xl font-bold text-green-600 mt-1" data-testid="individual-offer-price">
                    {offer.custom_price_eur} {offer.currency}
                  </p>
                </div>
                <div className="border-t border-blue-200 pt-4">
                  <span className="text-sm font-semibold text-gray-700">Platnosť ponuky do:</span>
                  <p className="text-lg font-bold text-gray-900 mt-1">
                    {new Date(offer.valid_until).toLocaleDateString('sk-SK', {
                      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-6">
              <div className="flex items-start">
                <span className="text-yellow-600 text-xl mr-3">⚠️</span>
                <div>
                  <p className="text-sm font-semibold text-yellow-900 mb-1">Dôležité upozornenie</p>
                  <p className="text-sm text-yellow-800">
                    Táto ponuka je určená iba pre váš účet a je možné ju použiť iba jedenkrát.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-bold text-gray-900 mb-4">Čo získate:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3"><span className="text-green-500 text-xl">✓</span>
                  <span className="text-gray-700">Prístup ku všetkým testom Testovanie 9 na školský rok {offer.school_year}</span></li>
                <li className="flex items-start gap-3"><span className="text-green-500 text-xl">✓</span>
                  <span className="text-gray-700">Vyhodnotenia a vysvetlenia</span></li>
                <li className="flex items-start gap-3"><span className="text-green-500 text-xl">✓</span>
                  <span className="text-gray-700">Platnosť do konca školského roka</span></li>
              </ul>
            </div>

            <div className="text-center">
              <button
                data-testid="individual-offer-pay-btn"
                onClick={handleProceedToPayment}
                className="w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-green-600 text-white font-bold text-lg rounded-xl hover:from-blue-700 hover:to-green-700 transition shadow-lg"
              >
                Pokračovať na platbu
              </button>
            </div>
            <p className="text-xs text-gray-500 text-center mt-4">
              Po kliknutí na tlačidlo budete presmerovaní na bezpečnú platobnú bránu Stripe.
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <button onClick={() => navigate('/')} className="text-gray-600 hover:text-gray-800 text-sm">
            ← Späť na hlavnú stránku
          </button>
        </div>
      </div>
    </div>
  )
}

const ErrorCard = ({ emoji, title, body, onBack }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
    <div className="max-w-md w-full">
      <div className="bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="text-6xl mb-4">{emoji}</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{title}</h1>
        <p className="text-gray-600 mb-6">{body}</p>
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold"
        >
          Späť na hlavnú stránku
        </button>
      </div>
    </div>
  </div>
)

export default IndividualOfferPage
